// src/api/eventApi.js
import api from "./api";

/**
 * Event API service
 * Provides methods for accessing event endpoints
 */

// ==================== EVENT CRUD ====================

/**
 * Create a new event
 * @param {Object} eventData - Event data
 * @param {string} eventData.name - Event name
 * @param {string} eventData.datetime - Event datetime in ISO format
 * @param {Array} eventData.tasks - Array of tasks
 * @param {File} eventData.file - Optional file attachment
 * @returns {Promise} Created event data
 */
export const createEvent = async (eventData) => {
  const formData = new FormData();
  formData.append("name", eventData.name);
  formData.append("datetime_str", eventData.datetime);

  // Tasks are now optional
  if (eventData.tasks && eventData.tasks.length > 0) {
    formData.append("tasks", JSON.stringify(eventData.tasks));
  }

  if (eventData.file) {
    formData.append("file", eventData.file);
  }

  const response = await api.post("/events/create", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.data;
};

/**
 * Update an existing event
 * @param {string} eventId - Event ID
 * @param {Object} eventData - Updated event data
 * @returns {Promise} Updated event data
 */
export const updateEvent = async (eventId, eventData) => {
  const formData = new FormData();

  if (eventData.name) {
    formData.append("name", eventData.name);
  }
  if (eventData.datetime) {
    formData.append("datetime_str", eventData.datetime);
  }
  if (eventData.tasks) {
    formData.append("tasks", JSON.stringify(eventData.tasks));
  }
  if (eventData.file) {
    formData.append("file", eventData.file);
  }

  const response = await api.put(`/events/update/${eventId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.data;
};

/**
 * Delete an event
 * @param {string} eventId - Event ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteEvent = async (eventId) => {
  const response = await api.delete(`/events/${eventId}`);
  return response.data.data;
};

// ==================== EVENT LISTING ====================

/**
 * Get all events (admin only)
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 10)
 * @param {string} params.search - Search query (optional)
 * @param {string} params.from_date - Filter from date YYYY-MM-DD (optional)
 * @param {string} params.to_date - Filter to date YYYY-MM-DD (optional)
 * @returns {Promise} Paginated events list
 */
export const getAllEvents = async (params = {}) => {
  const response = await api.get("/events/all", { params });
  return response.data.data;
};

/**
 * Get events assigned to current user
 * @returns {Promise} User's assigned events
 */
export const getAssignedEvents = async () => {
  const response = await api.get("/events/assigned");
  return response.data.data;
};

/**
 * Get single event detail
 * @param {string} eventId - Event ID
 * @returns {Promise} Event details
 */
export const getEventDetail = async (eventId) => {
  const response = await api.get(`/events/${eventId}`);
  return response.data.data;
};

/**
 * Get event detail with task completion statuses
 * @param {string} eventId - Event ID
 * @returns {Promise} Event details with task statuses
 */
export const getEventDetailWithStatus = async (eventId) => {
  const response = await api.get(`/events/${eventId}/detail-with-status`);
  return response.data.data;
};

// ==================== EVENT STATUS ====================

/**
 * Mark event as read
 * @param {string} eventId - Event ID
 * @returns {Promise} Updated status
 */
export const markEventAsRead = async (eventId) => {
  const response = await api.post(`/events/${eventId}/mark-read`);
  return response.data.data;
};

/**
 * Mark event as unread
 * @param {string} eventId - Event ID
 * @returns {Promise} Updated status
 */
export const markEventAsUnread = async (eventId) => {
  const response = await api.post(`/events/${eventId}/mark-unread`);
  return response.data.data;
};

// ==================== TASK COMPLETION STATUS ====================

/**
 * Mark task completion status for a user (Admin only)
 * @param {string} eventId - Event ID
 * @param {string} taskId - Task ID
 * @param {Object} data - Status data
 * @param {string} data.user_id - User ID
 * @param {string} data.status - Status: "completed" or "not_completed"
 * @returns {Promise} Updated status
 */
export const markTaskStatus = async (eventId, taskId, data) => {
  const response = await api.post(`/events/${eventId}/tasks/${taskId}/mark-status`, data);
  return response.data.data;
};

// ==================== STATISTICS ====================

/**
 * Get event statistics
 * For admins: overall statistics
 * For users: personal statistics
 * @returns {Promise} Event statistics
 */
export const getEventStatistics = async () => {
  const response = await api.get("/events/statistics/overview");
  return response.data.data;
};

export default {
  createEvent,
  updateEvent,
  deleteEvent,
  getAllEvents,
  getAssignedEvents,
  getEventDetail,
  getEventDetailWithStatus,
  markEventAsRead,
  markEventAsUnread,
  markTaskStatus,
  getEventStatistics,
};
